import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Container, Row, Col, Table, Alert, Spinner, Card, Button, Form, InputGroup, Badge } from 'react-bootstrap';
import Navbar from '../components/Navbar';
import EditArticolo from '../components/EditArticolo';
import { ThemeContext } from '../context/ThemeContext';
import { useAlert } from '../context/AlertContext';
import magService from '../services/magService';
import useIsMobile from '../hooks/useIsMobile';

const Magazzino = () => {
  const { darkMode } = useContext(ThemeContext);
  const { showAlertGlobal } = useAlert();
  
  // Stati per i dati
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false); // Nuovo stato per ricerca
  const [error, setError] = useState(null);
  const [loadingModifica, setLoadingModifica] = useState(null); // Per tracciare quale riga sta caricando
  
  // Stati per i filtri
  const [searchFilter, setSearchFilter] = useState(''); // Filtro per descrizione
  const [codiceFilter, setCodiceFilter] = useState(''); // Filtro per codice
  const [onlySuWeb, setOnlySuWeb] = useState(true); // Mostra solo articoli disponibili per PanifyCloud
  const [onlySuFlash, setOnlySuFlash] = useState(false);
  const isMobile = useIsMobile();

  // Stati per il modale articolo
  const [showArticoloModal, setShowArticoloModal] = useState(false);
  const [articoloDettagli, setArticoloDettagli] = useState(null);
  const [isNewArticolo, setIsNewArticolo] = useState(false);

  const loadArticoli = useCallback(async (searchParams = {}) => {
    try {
      // Se ci sono filtri oltre a su_web, mostra lo stato searching
      const hasAdditionalFilters = Object.entries(searchParams).some(([key]) => key !== 'su_web');
      if (hasAdditionalFilters) {
        setSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      console.log('🔍 Caricamento articoli con parametri:', searchParams);
      
      const data = await magService.getMagazzino(searchParams);
      
      console.log('✅ Articoli magazzino caricati:', data);
      
      // Verifica se i dati sono nell'array data.data
      const articoliArray = data?.data || data || [];
      
      // Ordina gli articoli per codice
      const sortedData = articoliArray.sort((a, b) => {
        const codeA = (a.cd_min || '').toString();
        const codeB = (b.cd_min || '').toString();
        return codeA.localeCompare(codeB);
      });
      
      setArticoli(sortedData);
    } catch (error) {
      console.error('Errore nel caricamento degli articoli:', error);
      setError('Errore nel caricamento degli articoli del magazzino');
      showAlertGlobal('Errore nel caricamento degli articoli', 'danger');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [showAlertGlobal]);

  // Costruisce i parametri di ricerca
  const getSearchParams = useCallback(() => {
    const searchParams = {};
    if (!isMobile) {
      if (searchFilter.trim()) {
        searchParams.searchText = searchFilter.trim();
        searchParams.searchField = 'descriz';
      } else if (codiceFilter.trim()) {
        searchParams.searchText = codiceFilter.trim();
        searchParams.searchField = 'cd_min';
      }
    }
    if (onlySuWeb) searchParams.su_web = '1';
    return searchParams;
  }, [isMobile, onlySuWeb, searchFilter, codiceFilter]);

  // Gestione del tasto Enter nei campi di ricerca (aggiornamento immediato)
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      loadArticoli(getSearchParams());
    }
  }, [loadArticoli, getSearchParams]);

  const handleToggleSuWeb = useCallback((checked) => {
    setOnlySuWeb(checked);
  }, []);

  // Aggiornamento automatico quando cambiano i filtri di ricerca (con debounce 400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadArticoli(getSearchParams());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchFilter, codiceFilter, onlySuWeb, getSearchParams, loadArticoli]);

  // Funzioni per svuotare i campi
  const clearSearchFilter = () => {
    setSearchFilter('');
  };

  const clearCodiceFilter = () => {
    setCodiceFilter('');
  };

  // Caricamento iniziale gestito da useEffect con getSearchParams (onlySuWeb=true di default)

  const articoliFiltrati = useMemo(() => {
    let result = articoli;
    if (onlySuWeb) {
      result = result.filter((articolo) => {
        const flag = articolo?.su_web ?? articolo?.suWeb ?? articolo?.SU_WEB ?? articolo?.suWEB ?? '0';
        return String(flag).trim() === '1';
      });
    }
    if (onlySuFlash) {
      result = result.filter((articolo) => {
        const flag = articolo?.su_flash ?? articolo?.suFlash ?? articolo?.SU_FLASH ?? articolo?.suFLASH ?? '0';
        return String(flag).trim() === '1';
      });
    }
    if (isMobile && searchFilter.trim()) {
      const q = searchFilter.toLowerCase().trim();
      result = result.filter((articolo) => {
        const descriz = (articolo.descriz || '').toLowerCase();
        const code = String(articolo.cd_min || '').toLowerCase();
        return descriz.includes(q) || code.includes(q);
      });
    }
    return result;
  }, [articoli, onlySuWeb, onlySuFlash, isMobile, searchFilter]);

  const handleModifica = async (articolo) => {
    console.log('Modifica articolo:', articolo);
    
    setIsNewArticolo(false); // Reset modalità nuovo articolo
    setLoadingModifica(articolo.cd_min);
    
    try {
      // Imposta i dettagli dell'articolo per il modale
      setArticoloDettagli(articolo);
      setShowArticoloModal(true);
      
    } catch (error) {
      console.error('Errore durante l\'apertura del modale modifica:', error);
      showAlertGlobal('Errore durante l\'apertura del modale', 'danger');
    } finally {
      setLoadingModifica(null);
    }
  };

  const handleNuovoArticolo = () => {
    console.log('Nuovo articolo richiesto');
    setIsNewArticolo(true);
    setArticoloDettagli(null);
    setShowArticoloModal(true);
  };

  const formatField = (value) => {
    return value || '-';
  };

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <Navbar />
      
      {!isMobile ? (
        // ===== VERSIONE DESKTOP =====
        <Container fluid className="mt-5 pt-3">
          <Row className="justify-content-center">
            <Col xs={12}>
              
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className={darkMode ? 'text-light' : 'text-dark'}>
                    <i className="fas fa-boxes me-2"></i>
                    Magazzino Prodotti
                  </h2>
                </div>
                <div>
                  <Button
                    variant="primary"
                    onClick={handleNuovoArticolo}
                    className="d-flex align-items-center"
                  >
                    <i className="fas fa-plus me-2"></i>
                    Nuovo Articolo
                  </Button>
                </div>
              </div>

              {/* Card Filtri */}
              <Card className="mb-4">
                <Card.Header className="card-header-sidebar-bg">
                  <h5 className="mb-0">
                    <i className="fas fa-filter me-2"></i>Filtri
                  </h5>
                </Card.Header>
                <Card.Body className={darkMode ? 'text-light' : ''}>
                  <Row className="g-3">
                    {/* Filtro per descrizione */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filtra per Descrizione</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-search"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca per descrizione..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                          {searchFilter && (
                            <Button
                              variant="outline-secondary"
                              onClick={clearSearchFilter}
                              className={darkMode ? "border-secondary text-light" : ""}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          )}
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Ricerca automatica (min. 3 caratteri)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    
                    {/* Filtro per codice */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filtra per Codice</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-barcode"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca per codice..."
                            value={codiceFilter}
                            onChange={(e) => setCodiceFilter(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                          {codiceFilter && (
                            <Button
                              variant="outline-secondary"
                              onClick={clearCodiceFilter}
                              className={darkMode ? "border-secondary text-light" : ""}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          )}
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Ricerca automatica (min. 3 caratteri)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                      <Form.Check
                        type="switch"
                        id="filtro-su-web"
                        label="Mostra solo articoli disponibili per PanifyCloud"
                        checked={onlySuWeb}
                        onChange={(e) => setOnlySuWeb(e.target.checked)}
                        className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                      />
                      {(searchFilter || codiceFilter || onlySuWeb || onlySuFlash) && (
                        <small className="text-muted text-nowrap mb-0">
                          <i className="fas fa-filter me-1"></i>
                          Filtri attivi
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <Form.Check
                        type="switch"
                        id="filtro-su-flash"
                        label="Mostra solo prodotti in promozione"
                        checked={onlySuFlash}
                        onChange={(e) => setOnlySuFlash(e.target.checked)}
                        className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                      />
                      {(searchFilter || codiceFilter || onlySuWeb || onlySuFlash) && (
                        <div className="d-flex flex-wrap justify-content-end gap-1">
                          {searchFilter && <Badge bg="primary">Descrizione: "{searchFilter}"</Badge>}
                          {codiceFilter && <Badge bg="info">Codice: "{codiceFilter}"</Badge>}
                          {onlySuWeb && <Badge bg="warning" text="dark">PanifyCloud</Badge>}
                          {onlySuFlash && <Badge bg="danger">Promozione</Badge>}
                        </div>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Contenuto principale */}
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p>Caricamento articoli in corso...</p>
                  </div>
                </div>
              ) : searching ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="success" className="mb-3" />
                    <p>Ricerca in corso...</p>
                  </div>
                </div>
              ) : error ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              ) : (
                <Card>
<Card.Header className="card-header-sidebar-bg">
                  <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Articoli Disponibili ({articoliFiltrati.length})
                      {(searchFilter || codiceFilter) && (
                        <small className="ms-2 text-muted">
                          (risultati filtrati)
                        </small>
                      )}
                    </h5>
                  </Card.Header>
                  <Card.Body className={darkMode ? 'text-light' : ''}>
                    {articoliFiltrati.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="text-muted">
                          {(searchFilter || codiceFilter) 
                            ? 'Nessun articolo trovato per i criteri di ricerca specificati.'
                            : 'Nessun articolo disponibile nel magazzino.'
                          }
                        </p>
                        {(searchFilter || codiceFilter) && (
                          <Button variant="outline-primary" onClick={() => {
                            setSearchFilter('');
                            setCodiceFilter('');
                            const params = onlySuWeb ? { su_web: '1' } : {};
                            loadArticoli(params);
                          }}>
                            <i className="fas fa-times me-1"></i>
                            Rimuovi filtri
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table 
                          striped 
                          bordered 
                          hover 
                          className={darkMode ? 'table-dark' : ''}
                        >
                          <thead className={darkMode ? 'table-dark' : 'table-light'}>
                            <tr>
                              <th>Codice</th>
                              <th>Descrizione</th>
                              <th>Unità Misura</th>
                              <th>Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {articoliFiltrati.map((articolo, index) => (
                              <tr key={articolo.cd_min || index}>
                                <td>
                                  <code className={`small ${darkMode ? 'text-warning' : 'text-primary'}`}>
                                    {formatField(articolo.cd_min)}
                                  </code>
                                </td>
                                <td>
                                  <strong>{formatField(articolo.descriz)}</strong>
                                </td>
                                <td>
                                  {formatField(articolo.unita_m)}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleModifica(articolo)}
                                    disabled={loadingModifica === articolo.cd_min}
                                    className="d-flex align-items-center"
                                  >
                                    {loadingModifica === articolo.cd_min ? (
                                      <>
                                        <Spinner size="sm" className="me-1" />
                                        <span>Caricamento...</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-edit me-1"></i>
                                        <span>Modifica</span>
                                      </>
                                    )}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            </Col>
          </Row>
        </Container>
      ) : (
        // ===== VERSIONE MOBILE: header + filtri fissi, solo cards scrollabili =====
        <div
          className="magazzino-mobile-shell"
          style={{
            backgroundColor: darkMode ? '#121212' : '#FFFEF9',
          }}
        >
          <div
            className="magazzino-mobile-sticky px-3 pt-2 pb-2"
            style={{
              backgroundColor: darkMode ? '#121212' : '#FFFEF9',
              borderBottom: `1px solid ${darkMode ? '#495057' : '#dee2e6'}`,
              boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <div className={`d-flex justify-content-between align-items-center mb-2 ${darkMode ? 'text-light' : 'text-dark'}`}>
              <h4 className="mb-0">
                <i className="fas fa-boxes me-2"></i>
                Magazzino
              </h4>
              <Button
                variant="primary"
                onClick={handleNuovoArticolo}
                className="d-flex align-items-center"
              >
                <i className="fas fa-plus me-1"></i>
                Nuovo
              </Button>
            </div>

            <InputGroup className="mb-2">
              <InputGroup.Text className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Cerca descrizione o codice..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                onKeyPress={handleKeyPress}
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
              {searchFilter && (
                <Button
                  variant="outline-secondary"
                  onClick={() => { setSearchFilter(''); setCodiceFilter(''); }}
                  className={darkMode ? 'border-secondary text-light' : ''}
                >
                  <i className="fas fa-times"></i>
                </Button>
              )}
            </InputGroup>

            <div className="mt-1">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <Form.Check
                  type="switch"
                  id="mobile-filtro-su-web"
                  label="Solo articoli PanifyCloud"
                  checked={onlySuWeb}
                  onChange={(e) => setOnlySuWeb(e.target.checked)}
                  className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                  style={{ minWidth: 0 }}
                />
                {(searchFilter || codiceFilter || onlySuWeb || onlySuFlash) && (
                  <small className="text-muted text-nowrap mb-0">
                    <i className="fas fa-filter me-1"></i>
                    Filtri attivi
                  </small>
                )}
              </div>
              <div className="d-flex align-items-center justify-content-between gap-2">
                <Form.Check
                  type="switch"
                  id="mobile-filtro-su-flash"
                  label="Solo prodotti in promozione"
                  checked={onlySuFlash}
                  onChange={(e) => setOnlySuFlash(e.target.checked)}
                  className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                  style={{ minWidth: 0 }}
                />
                {(searchFilter || codiceFilter || onlySuWeb || onlySuFlash) && (
                  <div className="d-flex flex-wrap justify-content-end gap-1">
                    {searchFilter && (
                      <Badge bg="primary">
                        Descrizione
                      </Badge>
                    )}
                    {codiceFilter && (
                      <Badge bg="info">
                        Codice
                      </Badge>
                    )}
                    {onlySuWeb && (
                      <Badge bg="warning" text="dark">
                        PanifyCloud
                      </Badge>
                    )}
                    {onlySuFlash && (
                      <Badge bg="danger">
                        Promozione
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="magazzino-mobile-scroll px-3 py-2">
            {loading && (
              <div className="text-center my-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </Spinner>
                <p className="mt-2">Caricamento articoli...</p>
              </div>
            )}

            {searching && (
              <div className="text-center my-4">
                <Spinner animation="border" variant="success" role="status">
                  <span className="visually-hidden">Ricerca...</span>
                </Spinner>
                <p className="mt-2">Ricerca in corso...</p>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {!loading && !searching && !error && (
              <>
                {articoli.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    Nessun articolo trovato nel magazzino
                  </Alert>
                ) : articoliFiltrati.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                    <p className="text-muted mb-3">
                      Nessun articolo trovato per i criteri di ricerca.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setSearchFilter('');
                        setCodiceFilter('');
                      }}
                    >
                      <i className="fas fa-times me-1"></i>
                      Rimuovi filtri
                    </Button>
                  </div>
                ) : (
                  <>
                    {articoliFiltrati.map((articolo, index) => (
                      <Card key={articolo.cd_min || index} className={`mb-2 ${darkMode ? 'text-light' : ''}`}>
                        <Card.Body className="p-3 d-flex align-items-center gap-2">
                          <div className="flex-grow-1 min-w-0">
                            <div className="mb-1">
                              <code className={darkMode ? 'text-warning' : 'text-primary'}>
                                {formatField(articolo.cd_min)}
                              </code>
                            </div>
                            <h6 className="mb-1" style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                              {formatField(articolo.descriz)}
                            </h6>
                            <div className="text-muted">
                              <small>U.M.: {formatField(articolo.unita_m)}</small>
                            </div>
                          </div>
                          <Button
                            variant="outline-primary"
                            className="magazzino-card-edit-btn flex-shrink-0"
                            onClick={() => handleModifica(articolo)}
                            disabled={loadingModifica === articolo.cd_min}
                          >
                            {loadingModifica === articolo.cd_min ? (
                              <Spinner as="span" animation="border" size="sm" />
                            ) : (
                              <>
                                <i className="fas fa-edit me-1"></i>
                                Modifica
                              </>
                            )}
                          </Button>
                        </Card.Body>
                      </Card>
                    ))}

                    <div className={`mt-2 mb-3 p-3 rounded ${darkMode ? 'bg-secondary text-light' : 'bg-light text-muted'}`}>
                      <div className="text-center">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          <strong>
                            {(searchFilter || codiceFilter)
                              ? `Trovati: ${articoliFiltrati.length} articoli`
                              : `Totale articoli: ${articoli.length}`}
                          </strong>
                        </small>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Componente EditArticolo */}
      <EditArticolo
        show={showArticoloModal}
        onHide={() => {
          setShowArticoloModal(false);
          setIsNewArticolo(false); // Reset modalità quando si chiude
        }}
        articoloDettagli={articoloDettagli}
        isNewArticolo={isNewArticolo}
        hidePrezziMagazzinoTabs={true}
        onArticoloUpdate={(articoloAggiornato) => {
          const messaggio = isNewArticolo ? 'Nuovo articolo creato con successo' : 'Articolo aggiornato con successo';
          showAlertGlobal(messaggio, 'success');
          loadArticoli(getSearchParams());
        }}
      />
    </div>
  );
};

export default Magazzino;
